/**
 * Preserves object and array references across {@link InjectedData} rebuilds when
 * individual entities are shallow-equal to their previous snapshot.
 *
 * This lets list rows (`LayoutList`) keep stable `layoutListRowItem` references
 * when unrelated slots (timer, current athlete, …) change on the parent payload.
 */

import { isDecimalLike } from "./resolution.js";
import type { AthleteData, InjectedData } from "./types.js";

type EntityWithId = { id: string | number };

/**
 * Compares two injected payload leaf values, treating Prisma Decimal instances as
 * equal when their numeric representation matches (adapters often allocate new
 * Decimal objects on every rebuild).
 */
export function injectedValuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (isDecimalLike(left) && isDecimalLike(right)) {
    return left.toString() === right.toString();
  }
  return false;
}

/**
 * Returns whether two values are shallow-equal plain objects (same keys, value-equal leaves).
 */
export function shallowEqualRecords(
  left: Record<string, unknown> | null | undefined,
  right: Record<string, unknown> | null | undefined,
): boolean {
  if (Object.is(left, right)) return true;
  if (left == null || right == null) return false;
  if (typeof left !== "object" || typeof right !== "object") return false;

  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;

  for (const key of leftKeys) {
    if (!Object.prototype.hasOwnProperty.call(right, key)) return false;
    if (!injectedValuesEqual(left[key], right[key])) return false;
  }

  return true;
}

/**
 * Reuses `previous` when it is shallow-equal to `next`.
 */
export function stabilizeRecord<T extends Record<string, unknown>>(
  previous: T | null | undefined,
  next: T | null | undefined,
): T | null | undefined {
  if (next === undefined) return undefined;
  if (next === null) {
    return previous === null ? previous : null;
  }
  if (
    previous != null &&
    shallowEqualRecords(
      previous as Record<string, unknown>,
      next as Record<string, unknown>,
    )
  ) {
    return previous;
  }
  return next;
}

/**
 * Reuses `previous` when it is shallow-equal to `next` and exposes an `id` field.
 */
export function stabilizeEntityById<T extends EntityWithId>(
  previous: T | null | undefined,
  next: T | null | undefined,
): T | null | undefined {
  if (next === undefined) return undefined;
  if (next === null) {
    return previous === null ? previous : null;
  }
  if (
    previous != null &&
    previous.id === next.id &&
    shallowEqualRecords(
      previous as unknown as Record<string, unknown>,
      next as unknown as Record<string, unknown>,
    )
  ) {
    return previous;
  }
  return next;
}

/**
 * Stabilizes nested relation records on an athlete before shallow comparison.
 *
 * Adapters rebuild `ageCategory`, `weightCategory`, and `discipline` on every pass
 * even when lift fields are unchanged, which would otherwise invalidate every row.
 */
export function stabilizeAthleteEntity(
  previous: AthleteData | null | undefined,
  next: AthleteData | null | undefined,
): AthleteData | null | undefined {
  if (next === undefined) return undefined;
  if (next === null) {
    return previous === null ? previous : null;
  }
  if (previous == null || previous.id !== next.id) {
    return next;
  }

  const merged: AthleteData = {
    ...next,
    ageCategory: (stabilizeRecord(
      previous.ageCategory as Record<string, unknown> | undefined,
      next.ageCategory as Record<string, unknown> | undefined,
    ) ?? next.ageCategory) as AthleteData["ageCategory"],
    weightCategory: (stabilizeRecord(
      previous.weightCategory as Record<string, unknown> | undefined,
      next.weightCategory as Record<string, unknown> | undefined,
    ) ?? next.weightCategory) as AthleteData["weightCategory"],
    discipline: (stabilizeRecord(
      previous.discipline as Record<string, unknown> | undefined,
      next.discipline as Record<string, unknown> | undefined,
    ) ?? next.discipline) as AthleteData["discipline"],
    powerscoutBests: (stabilizeRecord(
      previous.powerscoutBests as Record<string, unknown> | null | undefined,
      next.powerscoutBests as Record<string, unknown> | null | undefined,
    ) ?? next.powerscoutBests) as AthleteData["powerscoutBests"],
  };

  if (
    shallowEqualRecords(
      previous as unknown as Record<string, unknown>,
      merged as unknown as Record<string, unknown>,
    )
  ) {
    return previous;
  }

  return merged;
}

/**
 * Like {@link stabilizeEntityArrayById} but uses {@link stabilizeAthleteEntity} per item.
 */
export function stabilizeAthleteArrayById(
  previous: AthleteData[] | undefined,
  next: AthleteData[] | undefined,
): AthleteData[] | undefined {
  if (next === undefined) return undefined;
  if (next.length === 0) {
    return previous && previous.length === 0 ? previous : next;
  }

  const previousById = new Map<string | number, AthleteData>();
  if (previous) {
    for (const item of previous) {
      previousById.set(item.id, item);
    }
  }

  const stabilized: AthleteData[] = new Array(next.length);
  let canReusePreviousArray =
    previous != null && previous.length === next.length;

  for (let index = 0; index < next.length; index += 1) {
    const nextItem = next[index]!;
    const cached = previousById.get(nextItem.id);
    const item = stabilizeAthleteEntity(cached, nextItem) ?? nextItem;
    stabilized[index] = item;
    if (canReusePreviousArray && previous![index] !== item) {
      canReusePreviousArray = false;
    }
  }

  if (canReusePreviousArray && previous) {
    return previous;
  }

  return stabilized;
}

/**
 * Reuses the previous array reference when order and item references are unchanged.
 */
export function stabilizePrimitiveArray<T>(
  previous: T[] | undefined,
  next: T[] | undefined,
): T[] | undefined {
  if (next === undefined) return undefined;
  if (!previous || previous.length !== next.length) return next;

  for (let index = 0; index < next.length; index += 1) {
    if (!injectedValuesEqual(previous[index], next[index])) {
      return next;
    }
  }

  return previous;
}

/**
 * Stabilizes an array of entities that expose a stable `id`.
 *
 * Items keep their previous reference when their `id` and shallow fields are unchanged.
 * The array reference is reused when every slot still points to the same item reference.
 */
export function stabilizeEntityArrayById<T extends EntityWithId>(
  previous: T[] | undefined,
  next: T[] | undefined,
): T[] | undefined {
  if (next === undefined) return undefined;
  if (next.length === 0) {
    return previous && previous.length === 0 ? previous : next;
  }

  const previousById = new Map<string | number, T>();
  if (previous) {
    for (const item of previous) {
      previousById.set(item.id, item);
    }
  }

  const stabilized: T[] = new Array(next.length);
  let canReusePreviousArray =
    previous != null && previous.length === next.length;

  for (let index = 0; index < next.length; index += 1) {
    const nextItem = next[index]!;
    const cached = previousById.get(nextItem.id);
    const item =
      cached !== undefined &&
      shallowEqualRecords(
        cached as unknown as Record<string, unknown>,
        nextItem as unknown as Record<string, unknown>,
      )
        ? cached
        : nextItem;
    stabilized[index] = item;
    if (canReusePreviousArray && previous![index] !== item) {
      canReusePreviousArray = false;
    }
  }

  if (canReusePreviousArray && previous) {
    return previous;
  }

  return stabilized;
}

const INJECTED_DATA_KEYS = [
  "currentAthlete",
  "athletes",
  "group",
  "currentGroup",
  "groups",
  "currentMeet",
  "timer",
  "timerSeconds",
  "attemptTimers",
  "attemptTimerSeconds",
  "currentMovement",
  "attemptNumber",
  "placementIfSuccessful",
  "refereeLights",
  "nextLiftData",
  "records",
  "levels",
  "ageCategories",
  "disciplineAgeCategories",
  "disciplineWeightCategories",
  "weightCategories",
  "disciplines",
  "leagues",
  "organisation",
  "organisations",
  "themeColors",
  "displaySettings",
] as const satisfies readonly (keyof InjectedData)[];

/**
 * Merges a freshly built {@link InjectedData} payload with the previous snapshot,
 * reusing references for shallow-unchanged entities and list items.
 *
 * @param previous - Last payload returned to the layout renderer (if any).
 * @param next - Newly built payload from an adapter.
 * @returns Stabilized payload safe to pass to React consumers.
 */
export function stabilizeInjectedData(
  previous: InjectedData | undefined,
  next: InjectedData,
): InjectedData {
  if (!previous) return next;

  const stabilized: InjectedData = {};

  for (const key of INJECTED_DATA_KEYS) {
    const nextValue = next[key];
    if (nextValue === undefined) continue;

    switch (key) {
      case "athletes":
        (stabilized as Record<string, unknown>)[key] =
          stabilizeAthleteArrayById(previous.athletes, next.athletes);
        break;
      case "groups":
      case "records":
      case "levels":
      case "disciplineAgeCategories":
      case "disciplineWeightCategories":
      case "ageCategories":
      case "weightCategories":
      case "disciplines":
      case "leagues":
      case "organisations":
        (stabilized as Record<string, unknown>)[key] = stabilizeEntityArrayById(
          previous[key] as EntityWithId[] | undefined,
          nextValue as EntityWithId[],
        );
        break;
      case "currentAthlete":
        (stabilized as Record<string, unknown>)[key] = stabilizeAthleteEntity(
          previous.currentAthlete,
          next.currentAthlete ?? null,
        );
        break;
      case "group":
      case "currentGroup":
      case "currentMeet":
      case "organisation":
        (stabilized as Record<string, unknown>)[key] = stabilizeEntityById(
          previous[key] as EntityWithId | null | undefined,
          nextValue as EntityWithId | null | undefined,
        );
        break;
      case "attemptTimers":
      case "attemptTimerSeconds":
        (stabilized as Record<string, unknown>)[key] = stabilizePrimitiveArray(
          previous[key] as unknown[] | undefined,
          nextValue as unknown[],
        );
        break;
      case "refereeLights":
        (stabilized as Record<string, unknown>)[key] = stabilizePrimitiveArray(
          previous[key] as unknown[] | undefined,
          nextValue as unknown[],
        );
        break;
      case "nextLiftData":
        (stabilized as Record<string, unknown>)[key] = stabilizeRecord(
          previous[key] as unknown as
            | Record<string, unknown>
            | null
            | undefined,
          nextValue as unknown as Record<string, unknown> | null | undefined,
        );
        break;
      case "displaySettings":
      case "themeColors":
        (stabilized as Record<string, unknown>)[key] = stabilizeRecord(
          previous[key] as unknown as
            | Record<string, unknown>
            | null
            | undefined,
          nextValue as unknown as Record<string, unknown> | null | undefined,
        );
        break;
      default:
        (stabilized as Record<string, unknown>)[key] = Object.is(
          previous[key],
          nextValue,
        )
          ? previous[key]
          : nextValue;
        break;
    }
  }

  let canReusePreviousRoot = true;
  for (const key of INJECTED_DATA_KEYS) {
    if (next[key] === undefined) continue;
    if (!Object.is(previous[key], stabilized[key])) {
      canReusePreviousRoot = false;
      break;
    }
  }

  return canReusePreviousRoot ? previous : stabilized;
}
